import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import { LatestPostsComponent } from '../../sections/latest-posts/latest-posts.component';
import { LatestAlertsComponent } from '../../sections/latest-alerts/latest-alerts.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: 'app-delete-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
  ],
  template: `
    <h2 mat-dialog-title class="text-red-500">
      Confirm Delete
    </h2>

    <mat-dialog-content>
      Are you sure you want to delete this post?
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>
        Cancel
      </button>

      <button mat-button color="warn" [mat-dialog-close]="'confirm'">
        Delete
      </button>
    </mat-dialog-actions>
  `,
})
export class DeleteConfirmDialogComponent {}

@Component({
  selector: 'app-social-ai-repeat-post-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    LatestPostsComponent,
    LatestAlertsComponent,
  ],
  templateUrl: './social-ai-repeat-post-form.component.html',
  styleUrl: './social-ai-repeat-post-form.component.scss',
})
export class SocialAiRepeatPostFormComponent implements OnInit {
  @Input() formData: any = {} as any;
  @Output() onSubmitClbk = new EventEmitter<any>();

  repeatForm: FormGroup;
  postId: string | null = null;
  isEditMode = false;
  isDataLoaded = false;

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';

  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  private dialog = inject(MatDialog);

  id: string | null = null;
  nextId: string | null = null;
  prevId: string | null = null;

  tag: string | null = null;
  nextTag: string | null = null;
  prevTag: string | null = null;

  companyID: number = 0;
  postStatus: string = '';
  enableRepeat: number | null = null;

  enableEdit: number = 1;
  enableReuse: number = 1;
  enableDelete: number = 1;

  months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  days = Array.from({ length: 31 }, (_, i) => i + 1);

  weekdays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private location: Location,
  ) {
    this.repeatForm = this.fb.group({
      company_id: this.coreService.user?.companyID || 0,
      interval_days: [''],
      custom_repeat: [''],
      repeat_date: [''],

      repeat_month: [''],
      repeat_day: [''],
      repeat_weekday: [''],
      repeat_time: [''],

      enable_repeat: [true],
      ai_generated_repeat: [''],
      post_title: [''],
      post_description: [''],
    });

    if (this.formData) {
      this.repeatForm.patchValue(this.formData);
    }
  }

  ngOnInit(): void {
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();

    this.companyID = this.coreService.user?.companyID || 0;

    this.route.paramMap.subscribe(pm => {
      const id = pm.get('id');

      if (id) {
        this.postId = id;
        this.id = id;
        this.isDataLoaded = false;

        this.loadData(id);
        this.loadActions(id);
      } else {
        this.coreService.showSnackbar('Invalid post ID.');
        this.isDataLoaded = true;
      }
    });
  }

  loadData(id: string): void {
    const companyId = this.coreService.user?.companyID || 0;

    this.coreService
      .getData(`/repeats/preview/${companyId}/${id}`)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res || !res.data) {
          this.coreService.showSnackbar('Post not found.');
          this.isDataLoaded = true;
          return;
        }

        const interval_days =
          res.data.interval_days && res.data.interval_days !== 0
            ? res.data.interval_days
            : '';

        const repeat_date = res.data.repeat_date || null;

        this.repeatForm.patchValue({
          post_title: res.data.post_title,
          post_description: res.data.post_description,
          interval_days: interval_days,
          repeat_date: repeat_date,
          ai_generated_repeat: res.data.enable_repeat === 1,
          enable_repeat: res.data.enable_repeat === 1,
          custom_repeat: res.data.custom_repeat || (!interval_days && repeat_date ? 'weekly' : ''),
        });

        if (res.data.custom_repeat === 'monthly' && repeat_date) {
          const dateObj = new Date(repeat_date);

          this.repeatForm.patchValue({
            repeat_month: dateObj.getMonth() + 1,
            repeat_day: dateObj.getDate(),
            repeat_time: dateObj.toTimeString().slice(0, 5),
          });
        }

        if (res.data.custom_repeat === 'weekly' && repeat_date) {
          const dateObj = new Date(repeat_date);
          const weekday = (dateObj.getDay() + 6) % 7;

          this.repeatForm.patchValue({
            repeat_weekday: weekday,
            repeat_time: dateObj.toTimeString().slice(0, 5),
          });
        }

        this.isEditMode = !!(interval_days || repeat_date);
        this.isDataLoaded = true;
      });
  }

  onIntervalChange(value: string): void {
    if (value) {
      this.repeatForm.patchValue({
        custom_repeat: '',
        repeat_date: null,
      });
    }
  }

  onCustomChange(value: string): void {
    if (value) {
      this.repeatForm.patchValue({
        interval_days: '',
      });
    }
  }

  backToPrevPage(): void {
    this.location.back();
  }

  submit(): void {
    const formValue = this.repeatForm.getRawValue();
    const custom_repeat = formValue.custom_repeat;
    const interval_days = formValue.interval_days;

    if (!interval_days && !custom_repeat) {
      this.coreService.showSnackbar('Please select either repeat days or custom option.');
      return;
    }

    if (custom_repeat === 'weekly') {
      if (formValue.repeat_weekday === '' || !formValue.repeat_time) {
        this.coreService.showSnackbar('Please select weekday and time for weekly option.');
        return;
      }

      const now = new Date();
      const currentWeekday = (now.getDay() + 6) % 7;
      const targetWeekday = Number(formValue.repeat_weekday);

      let diff = targetWeekday - currentWeekday;

      if (diff < 0) {
        diff += 7;
      }

      const nextDate = new Date(now);
      nextDate.setDate(now.getDate() + diff);

      const [hours, minutes] = formValue.repeat_time.split(':');
      nextDate.setHours(Number(hours), Number(minutes), 0, 0);

      formValue.repeat_date = nextDate.toISOString().slice(0, 16);
      formValue.interval_days = 0;
    }

    if (custom_repeat === 'monthly') {
      if (!formValue.repeat_month || !formValue.repeat_day || !formValue.repeat_time) {
        this.coreService.showSnackbar('Please select month, day and time for monthly option.');
        return;
      }

      const year = new Date().getFullYear();
      const month = String(formValue.repeat_month).padStart(2, '0');
      const day = String(formValue.repeat_day).padStart(2, '0');
      const time = formValue.repeat_time;

      formValue.repeat_date = `${year}-${month}-${day}T${time}`;
      formValue.interval_days = 0;
    }

    if (custom_repeat === 'yearly') {
      if (!formValue.repeat_date) {
        this.coreService.showSnackbar('Please select a valid date & time for yearly option.');
        return;
      }

      formValue.interval_days = 0;
    }

    if (formValue.repeat_date) {
      const selectedDate = new Date(formValue.repeat_date);
      const now = new Date();

      if (selectedDate.getTime() < now.getTime()) {
        this.coreService.showSnackbar('The scheduled date and time cannot be in the past.');
        return;
      }
    }

    formValue.enable_repeat = this.isEditMode
      ? this.repeatForm.get('enable_repeat')?.value ? 1 : 0
      : 1;

    delete formValue.post_title;
    delete formValue.post_description;
    delete formValue.repeat_month;
    delete formValue.repeat_day;
    delete formValue.repeat_weekday;
    delete formValue.repeat_time;

    if (!this.postId) {
      this.coreService.showSnackbar('Missing post ID.');
      return;
    }

    this.coreService.postData(`repeats/store/${this.postId}`, formValue).subscribe({
      next: (res: any) => {
        if (res?.success === false && res?.message) {
          this.coreService.showSnackbar(res.message);
          return;
        }

        this.coreService.showSnackbar('Repeat post saved successfully.');
        this.onSubmitClbk.emit(res);
        this.router.navigate(['/posts']);
      },
      error: (err: any) => {
        const apiMsg = err?.error?.message || 'An error occurred while saving repeat post.';

        this.coreService.showSnackbar(apiMsg);
        console.error(err);
      },
    });
  }

  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      this.posts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') {
      this.alerts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);

    if (!raw) {
      this.alerts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Alert[] | null;

      this.alerts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
      this.alerts = [];
    }
  }

  viewAlert(alert: any): void {
    const targetId = alert.posting_queue_id;

    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: any): void {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;

    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post): number => item.id;

  trackByAlertId = (_: number, item: Alert): number => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;

    if (!img || img.src.endsWith(this.naPlaceholder)) {
      return;
    }

    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string): string {
    const t = (plat || '').toLowerCase().trim();

    switch (t) {
      case 'instagram':
        return 'text-fuchsia-600';

      case 'facebook':
        return 'text-blue-600';

      case 'x':
        return 'text-neutral-700';

      case 'youtube':
        return 'text-red-600';

      default:
        return 'text-gray-700';
    }
  }

  get isDeleteDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();

    return status !== 'draft';
  }

  loadActions(id: string): void {
    this.coreService
      .getData(`/post/actions/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (!res) {
            return;
          }

          this.postStatus = res.post_status ?? '';

          this.enableRepeat = typeof res.enable_repeat === 'number'
            ? res.enable_repeat
            : null;

          this.enableEdit = 1;
          this.enableReuse = 1;
          this.enableDelete = 1;

          this.nextId = res.next_id ?? null;
          this.nextTag = res.next_tag ?? null;

          this.prevId = res.prev_id ?? null;
          this.prevTag = res.prev_tag ?? null;
        },
        error: (err: any) => {
          console.error('Failed to load post actions:', err);
        },
      });
  }

  viewList(): void {
    this.router.navigate(['/posts']);
  }

  nextPost(): void {
    if (!this.nextId) {
      return;
    }

    this.router.navigate(['/repeat-post', this.nextId]);
  }

  prevPost(): void {
    if (!this.prevId) {
      return;
    }

    this.router.navigate(['/repeat-post', this.prevId]);
  }

  openDeleteConfirm(): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '380px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm' && this.id) {
        this.deletePost();
      }
    });
  }

  deletePost(): void {
    this.coreService.postData(`manual-post/deletePost/${this.id}`, {
      company_id: this.companyID,
    }).subscribe(() => {
      this.router.navigate(['/posts']);
    });
  }
}